import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import api from '../services/api';
import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import { FileOpener } from '@capacitor-community/file-opener';
import PortalLayout, { card, StateMsg, datumNL, datumTijdNL } from '../components/PortalLayout';

const SK = {
  nieuw: { bg: '#eff6ff', fg: '#1d4ed8' },
  in_behandeling: { bg: '#fff7ed', fg: '#c2410c' },
  open: { bg: '#fef2f2', fg: '#b91c1c' },
  afgehandeld: { bg: '#ecfdf5', fg: '#047857' },
  opgelost: { bg: '#ecfdf5', fg: '#047857' },
  afgerond: { bg: '#ecfdf5', fg: '#047857' },
  werkorder_aangemaakt: { bg: '#f5f3ff', fg: '#6d28d9' },
  doorgestuurd: { bg: '#f5f3ff', fg: '#6d28d9' },
};
const TKD = { bg: '#f1f5f9', fg: '#475569' };
const TK = {
  storing: { bg: '#fef2f2', fg: '#b91c1c' },
  onderhoud: { bg: '#eff6ff', fg: '#1d4ed8' },
  werkorder: { bg: '#ecfdf5', fg: '#047857' },
  inspectie: { bg: '#f5f3ff', fg: '#6d28d9' },
  reparatie: { bg: '#fff7ed', fg: '#c2410c' },
  bezoek: TKD,
  notitie: TKD,
};
function Badge({ status }) {
  if (!status) return null;
  const k = SK[status] || { bg: '#f1f5f9', fg: '#475569' };
  return <span style={{ background: k.bg, color: k.fg, fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 6, whiteSpace: 'nowrap' }}>{status}</span>;
}
function Sectie({ titel, count, children }) {
  return (
    <div style={{ marginTop: 22 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.4 }}>{titel}{count != null ? ' (' + count + ')' : ''}</div>
      {children}
    </div>
  );
}

export default function ObjectDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [pdfBusy, setPdfBusy] = useState(null);
  useEffect(() => {
    api.get('/klantportaal/objecten/' + id)
      .then((r) => setData(r.data))
      .catch(() => setError('Kon object niet laden'));
  }, [id]);

  if (error || !data) {
    return (<PortalLayout titel="Object"><StateMsg loading={!data && !error} error={error} /></PortalLayout>);
  }

  const downloadPdf = async (w) => {
    setPdfBusy(w.id);
    try {
      const r = await api.get('/klantportaal/werkorders/' + w.id + '/pdf', { responseType: 'blob' });
      const naam = 'werkbon-' + (w.ordernummer || w.id) + '.pdf';
      if (Capacitor.isNativePlatform()) {
        const base64 = await new Promise((res, rej) => { const fr = new FileReader(); fr.onload = () => res(String(fr.result).split(',')[1]); fr.onerror = rej; fr.readAsDataURL(r.data); });
        const file = await Filesystem.writeFile({ path: naam, data: base64, directory: Directory.Cache });
        try { await FileOpener.open({ filePath: file.uri, contentType: 'application/pdf', openWithDefault: false }); }
        catch (e) { await Share.share({ title: naam, url: file.uri }); }
      } else {
        const url = URL.createObjectURL(r.data);
        const a = document.createElement('a'); a.href = url; a.download = naam; a.click(); URL.revokeObjectURL(url);
      }
    } catch (e) { if (!/canceled|cancelled/i.test(String(e?.message))) alert('Kon werkbon niet laden'); }
    setPdfBusy(null);
  };
  const o = data.object;
  const meldingen = [...(data.storingen || []), ...(data.meldingen || [])].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  const openCount = meldingen.filter((s) => !['afgehandeld', 'opgelost', 'afgerond', 'afgewezen'].includes(s.status)).length;
  const volgende = (data.onderhoud || []).map((x) => x.volgende_datum).filter(Boolean).sort()[0];

  const meta = { color: '#64748b', fontSize: 13 };
  const tegel = { flex: 1, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '12px 8px', textAlign: 'center' };
  const num = { fontSize: 22, fontWeight: 800, color: '#0f172a' };
  const lbl = { fontSize: 11, color: '#94a3b8', marginTop: 2 };

  return (
    <PortalLayout titel={o.naam || 'Object'}>
      <div style={card}>
        <div style={{ fontWeight: 700, color: '#1e293b', fontSize: 16 }}>{o.naam}</div>
        <div style={{ ...meta, marginTop: 4 }}>{[o.type, o.locatie].filter(Boolean).join(' \u00b7 ')}</div>
        {o.serienummer && <div style={{ ...meta, marginTop: 4 }}>S/N: {o.serienummer}</div>}
        {o.notities && <div style={{ color: '#475569', fontSize: 13, marginTop: 8, whiteSpace: 'pre-wrap' }}>{o.notities}</div>}
      </div>

      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <div style={tegel}><div style={num}>{openCount}</div><div style={lbl}>open storingen</div></div>
        <div style={tegel}><div style={num}>{(data.werkorders || []).length}</div><div style={lbl}>werkorders</div></div>
        <div style={tegel}><div style={{ ...num, fontSize: 14, paddingTop: 5 }}>{volgende ? datumNL(volgende) : '\u2014'}</div><div style={lbl}>volgend onderhoud</div></div>
      </div>

      <Sectie titel="Storingen & meldingen" count={meldingen.length}>
        {meldingen.length === 0 ? <div style={meta}>Geen storingen.</div> : meldingen.map((s) => (
          <div key={'s' + s.id} style={card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
              <div style={{ fontWeight: 600, color: '#1e293b' }}>{s.titel || 'Storing'}</div>
              <Badge status={s.status} />
            </div>
            <div style={{ ...meta, marginTop: 6 }}>{datumNL(s.created_at)}</div>
          </div>
        ))}
      </Sectie>

      <Sectie titel="Onderhoudsplanning" count={(data.onderhoud || []).length}>
        {(data.onderhoud || []).length === 0 ? <div style={meta}>Geen geplande beurten.</div> : data.onderhoud.map((x) => (
          <div key={'o' + x.id} style={card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
              <div style={{ fontWeight: 600, color: '#1e293b' }}>{[x.frequentie, x.dienst_type].filter(Boolean).join(' \u00b7 ') || 'Onderhoud'}</div>
              <span style={{ background: '#ecfdf5', color: '#047857', fontSize: 12, fontWeight: 600, padding: '3px 8px', borderRadius: 6, whiteSpace: 'nowrap' }}>{datumNL(x.volgende_datum)}</span>
            </div>
            {x.ordernummer && <div style={{ ...meta, marginTop: 4 }}>order {x.ordernummer}</div>}
          </div>
        ))}
      </Sectie>

      <Sectie titel="Werkorders" count={(data.werkorders || []).length}>
        {(data.werkorders || []).length === 0 ? <div style={meta}>Geen werkorders.</div> : data.werkorders.map((w) => (
          <div key={'w' + w.id} style={{ ...card, cursor: w.status === 'afgerond' ? 'pointer' : 'default' }}
            onClick={w.status === 'afgerond' ? () => downloadPdf(w) : undefined}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
              <div style={{ fontWeight: 600, color: '#1e293b' }}>{w.titel || 'Werkorder'}</div>
              <Badge status={w.status} />
            </div>
            <div style={{ ...meta, marginTop: 6 }}>{[w.ordernummer ? 'order ' + w.ordernummer : null, w.afgerond_op ? 'afgerond ' + datumNL(w.afgerond_op) : datumNL(w.created_at)].filter(Boolean).join(' \u00b7 ')}</div>
            {w.status === 'afgerond' && <div style={{ marginTop: 8, fontSize: 13, fontWeight: 600, color: '#00D4AA' }}>{pdfBusy === w.id ? 'Werkbon laden…' : '\uD83D\uDCC4 Werkbon openen'}</div>}
          </div>
        ))}
      </Sectie>

      <Sectie titel="Logboek" count={(data.logboek || []).length}>
        {(data.logboek || []).length === 0 ? <div style={meta}>Geen logboek-items.</div> : data.logboek.map((l) => (
          <div key={'l' + l.id} style={card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 4 }}>
              <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 6, textTransform: 'uppercase', background: (TK[l.type] || TKD).bg, color: (TK[l.type] || TKD).fg }}>{l.type}</span>
              <span style={{ ...meta, fontSize: 12 }}>{datumTijdNL(l.created_at)}</span>
            </div>
            <div style={{ color: '#1e293b', fontSize: 14, whiteSpace: 'pre-wrap' }}>{l.beschrijving}</div>
          </div>
        ))}
      </Sectie>
    </PortalLayout>
  );
}
